// @ts-nocheck
// Disable typechecking in this file so we can migrate it piecemeal, due to the file's sheer size
// While typing these functions in developement, remove the @ts-nocheck directive so you can see potential code errors/squigglies,
// and re-add it when you're finished.
import 'isomorphic-fetch';
import { stringify } from 'query-string';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';

import { VRSNL_MERCHANTID } from 'constants/crossSiteMerchantIdMapping';
import {
  MAFIA_AGENT_AUTH_REQ,
  MAFIA_AT_MAIN,
  MAFIA_AUTH_REQ,
  MAFIA_CAPTCHA_ANSWER,
  MAFIA_CAPTCHA_AUDIO_REQ,
  MAFIA_CAPTCHA_REQ,
  MAFIA_CAPTCHA_TOKEN,
  MAFIA_EMAIL_TOKEN,
  MAFIA_RECOGNIZED_TOKEN,
  MAFIA_SESSION_ID,
  MAFIA_SESSION_REQ,
  MAFIA_SESSION_TOKEN,
  MAFIA_UBID_MAIN
} from 'constants/apis';
import timedFetch from 'middleware/timedFetch';
import { cookieObjectToString } from 'helpers/Cookie';
import { formatCreditCard, formatCreditCardExpiration } from 'helpers/MyAccountUtils';
import { buildErrorQueryString, inIframe } from 'helpers/CheckoutUtils';
import { ENDPOINT_OPAL_EXPLICITFITS } from 'apis/opal';
import { recordToSplunk } from 'apis/checkout';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import { setSessionCookies } from 'actions/session';
import { fetchApiNotAuthenticatedMiddleware, fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import { devLogger } from 'middleware/logger';
import marketplace from 'cfg/marketplace.json';
import { AppState } from 'types/app';
import { Cookies } from 'types/cookies';
import { JanusParams, Mafia, OrderQueryParams, OrdersResponse, Recos, SearchOrderQueryParams } from 'types/mafia';
import { isMultiMerchant } from 'helpers/CartUtils';
import {
  BoundingBoxApiResponse,
  ImageMatchSearchResponse,
  UploadImageResponse
} from 'components/VisualSearch/VisualSearchResults/visualSearchResults.types';

const {
  cart: { cartName },
  merchantId
} = marketplace;

const TELL_A_FRIEND_ENDPOINT = '/email/v1/tellAFriend';
// TODO Update this once mafia slotz changes for report an error are deployed
const REPORT_AN_ERROR_ENDPOINT = '/email/v1/reportAnError';

/**
 * Given credentials, returns true if auth headers should be passed or false if
 * cookie auth should be requested.
 * @param  {Object} [credentials={}] mafia authentication credentials
 * @return {boolean}                 if auth headers should be passed
 */
export const authHeadersInUse = (credentials: Cookies = {}, queryParamCredentials = {}) =>
  !!(credentials['at-main'] || queryParamCredentials['auth_token']);

/**
 * Adds the given header to the header request object if the corresponding cookie exists.  If the cookie does not exist, no header is added to request object.
 *
 * This is necessary since Edge 14 barfs on fetch headers that are blank (See https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/8546263/).
 *
 * Mutates headers parameter.
 * @param {Object} headers map of headers name to values to include in the http request
 * @param {Object} cookies map of cookies names to values available in the execution context
 * @param {String} mafiaHeaderName the header name to put the cookie value
 * @param {String} cookieName the cookie name to lookup in `cookies` and put into `headers` if it exists.
 */
function translateCookieToMafiaHeader(headers, cookies, mafiaHeaderName, cookieName) {
  if (cookies[cookieName]) {
    headers[mafiaHeaderName] = cookies[cookieName];
  }
  return headers;
}

/**
 * Adds the given header to the header request object if the corresponding query param exists.  If the param does not exist, no header is added to request object.
 *
 * See Edge 14 note in translateCookieToMafiaHeader()
 *
 * Mutates headers parameter.
 * @param {Object} headers map of headers name to values to include in the http request
 * @param {Object} queryParams map of query param names to values available in the execution context
 * @param {String} mafiaHeaderName the header name to put the query param value
 * @param {String} paramName the query param name to lookup in `queryParams` and put into `headers` if it exists.
 */
function translateQueryParamToMafiaHeader(headers, queryParams, mafiaHeaderName, paramName) {
  if (queryParams[paramName]) {
    headers[mafiaHeaderName] = queryParams[paramName];
  }
  return headers;
}

/**
 * Given headers and credentials, return a new set of headers which includes
 * the appropriate auth-related headers.
 * @param  {Object} [initHeaders={}] inital set of headers
 * @param  {Object} [credentials={}] mafia auth credentials
 * @return {Object}                  init headers with auth headers
 */
export function withAuthHeaders(initHeaders = {}, credentials: Cookies = {}, queryParamCredentials = {}) {
  const headers = { ...initHeaders };
  headers[MAFIA_SESSION_REQ] = 'true';
  // headers for session-reliant mafia calls
  translateCookieToMafiaHeader(headers, credentials, MAFIA_SESSION_ID, 'session-id');
  translateCookieToMafiaHeader(headers, credentials, MAFIA_SESSION_TOKEN, 'session-token');
  translateCookieToMafiaHeader(headers, credentials, MAFIA_UBID_MAIN, 'ubid-main');
  translateCookieToMafiaHeader(headers, credentials, MAFIA_RECOGNIZED_TOKEN, 'x-main');
  translateQueryParamToMafiaHeader(headers, queryParamCredentials, MAFIA_EMAIL_TOKEN, 'auth_token');
  headers.Cookie = cookieObjectToString(credentials);

  if (!Object.keys(credentials).length) {
    devLogger('WARNING: You must send credential headers with your mafia request!');
  }

  if (authHeadersInUse(credentials)) {
    translateCookieToMafiaHeader(headers, credentials, MAFIA_AT_MAIN, 'at-main');
  } else {
    headers[MAFIA_AUTH_REQ] = true;
  }

  // CSC agents - only supporting client side calls with this
  if (inIframe()) {
    headers[MAFIA_AGENT_AUTH_REQ] = true;
    delete headers[MAFIA_AUTH_REQ];
    delete headers[MAFIA_SESSION_REQ];
  }
  return headers;
}

function sendJsonDataWithCreds(url, data, options = {}) {
  const { credentials, queryParamCredentials, method = 'post', fetcher, additionalHeaders = {} } = options;
  return fetcher(
    url,
    fetchOpts(
      {
        method,
        headers: { 'Content-Type': 'application/json', ...additionalHeaders },
        body: JSON.stringify(data)
      },
      credentials,
      queryParamCredentials
    )
  );
}

export function chromeAbandonedCartRedeem(
  { url }: Mafia,
  chromeToken,
  credentials: Cookies = {},
  fetcher = timedFetch('chromeAbandonedCartRedeem')
) {
  return sendJsonDataWithCreds(
    `${url}/chrome-abandoned-cart/redeem`,
    {},
    {
      credentials,
      method: 'POST',
      fetcher,
      additionalHeaders: {
        'X-Chrome-Abandoned-Cart-Token': chromeToken,
        'X-Marty-Env-Id': window.location.hostname
      }
    }
  );
}

/** Zappos Influencer APIs starts */
const commonInfluencerServiceHeaders = () => ({
  'X-Marty-InfAppEnv-Id': window.location.hostname
});

export function enrollInfluencer({ url }: Mafia, data, credentials: Cookies = {}, fetcher = timedFetch('enrollInfluencer')) {
  return sendJsonDataWithCreds(`${url}/influencer/enroll`, data, {
    credentials,
    fetcher,
    method: 'post',
    additionalHeaders: commonInfluencerServiceHeaders()
  });
}

export function getInfluencerAppConfigurations({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getInfluencerAppConfigurations')) {
  return fetcher(
    `${url}/influencer/apps`,
    fetchOpts(
      {
        headers: commonInfluencerServiceHeaders()
      },
      credentials
    )
  );
}

export function getInfluencerStatus({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getInfluencerStatus')) {
  return fetcher(
    `${url}/influencer/status`,
    fetchOpts(
      {
        headers: commonInfluencerServiceHeaders()
      },
      credentials
    )
  );
}

export function getInfluencerFollowersStatus(
  { url }: Mafia,
  influencerId,
  credentials: Cookies = {},
  fetcher = timedFetch('getInfluencerFollowersStatus')
) {
  return fetcher(
    `${url}/influencer/followStatus/${influencerId}`,
    fetchOpts(
      {
        headers: commonInfluencerServiceHeaders()
      },
      credentials
    )
  );
}

export function updateInfluencerFollowingStatus(
  { url }: Mafia,
  data,
  influencerId,
  credentials: Cookies = {},
  fetcher = timedFetch('updateInfluencerFollowingStatus')
) {
  return sendJsonDataWithCreds(`${url}/influencer/updateFollowStatus`, data, {
    credentials,
    fetcher,
    method: 'post',
    additionalHeaders: commonInfluencerServiceHeaders()
  });
}

export function getInfluencerDetails({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getInfluencerDetails')) {
  return fetcher(
    `${url}/influencer/details`,
    fetchOpts(
      {
        headers: commonInfluencerServiceHeaders()
      },
      credentials
    )
  );
}

export function getInfluencerDetailsByIdentifier(
  { url }: Mafia,
  identifier,
  identifierType,
  credentials: Cookies = {},
  host,
  fetcher = timedFetch('getInfluencerDetailsByIdentifier')
) {
  const headers = host ? { 'X-Marty-InfAppEnv-Id': host } : commonInfluencerServiceHeaders();
  return fetcher(
    `${url}/influencer/details/${identifier}?identifierType=${identifierType}`,
    fetchOpts(
      {
        headers
      },
      credentials
    )
  );
}

export function getInfluencerDetailsByIdentifierOnServer(
  { url }: Mafia,
  identifier,
  identifierType,
  credentials: Cookies = {},
  host,
  fetcher = timedFetch('getInfluencerDetailsByIdentifierOnServer')
) {
  return fetcher(
    `${url}/influencer/details/${identifier}?identifierType=${identifierType}`,
    fetchOpts(
      {
        headers: { 'X-Marty-InfAppEnv-Id': host }
      },
      credentials
    )
  );
}

export function getInfluencerStyles({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getInfluencerStyles')) {
  return fetcher(
    `${url}/influencer/styles`,
    fetchOpts(
      {
        headers: commonInfluencerServiceHeaders()
      },
      credentials
    )
  );
}

export function validateProfileHandle({ url }: Mafia, profileHandle, credentials: Cookies = {}, fetcher = timedFetch('validateProfileHandle')) {
  return fetcher(
    `${url}/influencer/uniqueHandle/${profileHandle}`,
    fetchOpts(
      {
        headers: commonInfluencerServiceHeaders()
      },
      credentials
    )
  );
}

export function getInfluencerToken({ url }: Mafia, data, credentials: Cookies = {}, fetcher = timedFetch('getInfluencerToken')) {
  return sendJsonDataWithCreds(`${url}/influencer/links`, data, {
    credentials,
    fetcher,
    method: 'post',
    additionalHeaders: commonInfluencerServiceHeaders()
  });
}

export function getConfirmPendingStatus({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getConfirmPendingStatus')) {
  return fetcher(
    `${url}/influencer/confirmPendingStatus`,
    fetchOpts(
      {
        method: 'post',
        headers: commonInfluencerServiceHeaders()
      },
      credentials
    )
  );
}

export function getConfirmDeclineStatus({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getConfirmDeclineStatus')) {
  return fetcher(
    `${url}/influencer/confirmDeclineStatus`,
    fetchOpts(
      {
        method: 'post',
        headers: commonInfluencerServiceHeaders()
      },
      credentials
    )
  );
}

export function getInfluencerSummaryReport(
  { url }: Mafia,
  credentials: Cookies = {},
  timePeriod,
  fetcher = timedFetch('getInfluencerSummaryReport')
) {
  return fetcher(
    `${url}/influencer/reporting/summary?period=${timePeriod}`,
    fetchOpts(
      {
        method: 'get',
        headers: commonInfluencerServiceHeaders()
      },
      credentials
    )
  );
}

export function getInfluencerProductReport(
  { url }: Mafia,
  credentials: Cookies = {},
  timePeriod,
  page,
  pageLength,
  fetcher = timedFetch('getInfluencerProductReport')
) {
  return fetcher(
    `${url}/influencer/reporting/products?period=${timePeriod}&page=${page}&pageLength=${pageLength}`,
    fetchOpts(
      {
        method: 'get',
        headers: commonInfluencerServiceHeaders()
      },
      credentials
    )
  );
}

export function getInfluencerCollectionReport(
  { url }: Mafia,
  credentials: Cookies = {},
  timePeriod,
  page,
  pageLength,
  fetcher = timedFetch('getInfluencerCollectionReport')
) {
  return fetcher(
    `${url}/influencer/reporting/collections?period=${timePeriod}&page=${page}&pageLength=${pageLength}`,
    fetchOpts(
      {
        method: 'get',
        headers: commonInfluencerServiceHeaders()
      },
      credentials
    )
  );
}

export function addInfluencerSocialProfile({ url }: Mafia, data, credentials: Cookies = {}, fetcher = timedFetch('addInfluencerSocialProfile')) {
  return sendJsonDataWithCreds(`${url}/influencer/addsocialprofile`, data, {
    credentials,
    fetcher,
    method: 'post',
    additionalHeaders: commonInfluencerServiceHeaders()
  });
}

export function updateInfluencerSocialProfile(
  { url }: Mafia,
  data,
  credentials: Cookies = {},
  fetcher = timedFetch('updateInfluencerSocialProfile')
) {
  return sendJsonDataWithCreds(`${url}/influencer/updatesocialprofile`, data, {
    credentials,
    fetcher,
    method: 'post',
    additionalHeaders: commonInfluencerServiceHeaders()
  });
}

export function updateInfluencerDetails({ url }: Mafia, data, credentials: Cookies = {}, fetcher = timedFetch('updateInfluencerDetails')) {
  return sendJsonDataWithCreds(`${url}/influencer/details`, data, {
    credentials,
    fetcher,
    method: 'post',
    additionalHeaders: commonInfluencerServiceHeaders()
  });
}
/** Zappos Influencer Content APIs */

export function getPresignedUrl({ url }: Mafia, data, credentials: Cookies = {}, fetcher = timedFetch('getPresignedUrl')) {
  return sendJsonDataWithCreds(`${url}/influencer/media/create/presignedurl`, data, {
    credentials,
    fetcher,
    method: 'post',
    additionalHeaders: commonInfluencerServiceHeaders()
  });
}

export function uploadToMediaCentral({ url }: Mafia, data, credentials: Cookies = {}, fetcher = timedFetch('uploadToMediaCentral')) {
  return sendJsonDataWithCreds(`${url}/influencer/media/upload`, data, {
    credentials,
    fetcher,
    method: 'post',
    additionalHeaders: commonInfluencerServiceHeaders()
  });
}

export function uploadToVMS({ url }: Mafia, data, credentials: Cookies = {}, fetcher = timedFetch('uploadToVMS')) {
  return sendJsonDataWithCreds(`${url}/influencercontent/content/upload/video`, data, {
    credentials,
    fetcher,
    method: 'post'
  });
}

export function getVideoFromVMS({ url }: Mafia, mediaId, credentials: Cookies = {}, fetcher = timedFetch('getVideoFromVMS')) {
  return fetcher(
    `${url}/influencercontent/content/video/${mediaId}`,
    fetchOpts(
      {
        method: 'get'
      },
      credentials
    )
  );
}

export function putInfluencerMedia(url, file, credentials: Cookies = {}, fetcher = timedFetch('PutInfluencerMedia')) {
  return fetcher(
    url,
    fetchOpts(
      {
        method: 'put',
        body: file
      },
      credentials
    )
  );
}

export function createOrUpdateShoppablePost(
  { url }: Mafia,
  data,
  method,
  credentials: Cookies = {},
  fetcher = timedFetch('saveAndSubmitShoppablePost')
) {
  return sendJsonDataWithCreds(`${url}/influencercontent/posts`, data, {
    credentials,
    fetcher,
    method
  });
}

export function getOrDeleteShoppablePost(
  { url }: Mafia,
  postId,
  postState,
  method,
  credentials: Cookies = {},
  fetcher = timedFetch('discardShoppablePost')
) {
  return fetcher(
    `${url}/influencercontent/posts/${postId}/${postState}`,
    fetchOpts(
      {
        method
      },
      credentials
    )
  );
}

export function getAllShoppablePosts(
  { url }: Mafia,
  influencerId,
  queryString = '',
  authRequired: boolean,
  credentials: Cookies = {},
  fetcher = timedFetch('getAllShoppablePosts')
) {
  const shoppablePostUrl = authRequired
    ? `${url}/influencercontent/posts/influencer/${influencerId}?${queryString}`
    : `${url}/influencercontent/n/posts/influencer/${influencerId}/approved?${queryString}`;

  return fetcher(
    shoppablePostUrl,
    fetchOpts(
      {
        method: 'get'
      },
      credentials
    )
  );
}

export function performInfluencerContentAction(
  { url }: Mafia,
  contentId,
  data,
  action: InfluencerContentAPIAction,
  credentials: Cookies = {},
  fetcher = timedFetch('hideInfluencerContent')
) {
  return sendJsonDataWithCreds(`${url}/influencercontent/content/${action}/${contentId}`, data, {
    credentials,
    fetcher,
    method: 'post'
  });
}

export function createAndSubmitInfluencerContent(
  { url }: Mafia,
  data,
  credentials: Cookies = {},
  fetcher = timedFetch('createAndSubmitInfluencerContent')
) {
  return sendJsonDataWithCreds(`${url}/influencercontent/content`, data, {
    credentials,
    fetcher,
    method: 'post'
  });
}

export function getAllInfluencerContent(
  { url }: Mafia,
  influencerId,
  queryString = '',
  authRequired: boolean,
  credentials: Cookies = {},
  fetcher = timedFetch('getAllInfluencerContent')
) {
  const getAllContentUrl = authRequired
    ? `${url}/influencercontent/content/influencer/${influencerId}?${queryString}`
    : `${url}/influencercontent/n/content/influencer/${influencerId}/approved?${queryString}`;
  return fetcher(
    getAllContentUrl,
    fetchOpts(
      {
        method: 'get'
      },
      credentials
    )
  );
}

export function getPostsLikeStatus({ url }: Mafia, queryString = '', credentials: Cookies = {}, fetcher = timedFetch('getPostsLikeStatus')) {
  const getAllPostIdStatusUrl = `${url}/influencercontent/likeStatus/posts?${queryString}`;
  return fetcher(
    getAllPostIdStatusUrl,
    fetchOpts(
      {
        method: 'get'
      },
      credentials
    )
  );
}

export function updatePostLikeStatus({ url }: Mafia, postId, method, credentials: Cookies = {}, fetcher = timedFetch('updatePostLikeStatus')) {
  return fetcher(
    `${url}/influencercontent/likeStatus/${postId}`,
    fetchOpts(
      {
        method
      },
      credentials
    )
  );
}

export function getApprovedShoppablePost({ url }: Mafia, postId, credentials: Cookies = {}, fetcher = timedFetch('getShoppablePost')) {
  return fetcher(
    `${url}/influencercontent/n/posts/${postId}/approved`,
    fetchOpts(
      {
        method: 'get'
      },
      credentials
    )
  );
}

export function searchInfluencerContent(
  { url }: Mafia,
  queryString = '',
  credentials: Cookies = {},
  fetcher = timedFetch('searchInfluencerContent')
) {
  return fetcher(`${url}/influencercontent/n/content/search?${queryString}`, fetchOpts({ method: 'get' }, credentials));
}

export function getAllContentWithPagination({ url }: Mafia, queryString = '', credentials: Cookies = {}, fetcher = timedFetch('paginatedResponse')) {
  return fetcher(`${url}/influencercontent/n/content/approved?${queryString}`, fetchOpts({ method: 'get' }, credentials));
}

/** Zappos Influencer APIs ends */

// Outfit Recommendations and VTO APIs

export function getOutfitRecos(
  { url }: Mafia,
  styleId,
  isOutfitsOccasionModelEnabled = false,
  credentials: Cookies = {},
  fetcher = timedFetch('getOutfitRecos')
) {
  const params = new URLSearchParams({ styleId: styleId });
  if (isOutfitsOccasionModelEnabled) {
    params.set('model', 'occasion');
  }
  return fetcher(`${url}/slotz/fashion/outfitRecommendations?${params}`, fetchOpts({}, credentials));
}

export function getProductComparisonTable({ url }: Mafia, styleId, credentials: Cookies = {}, fetcher = timedFetch('getProductComparisonTable')) {
  return fetcher(`${url}/slotz/fashion/getProductComparisonContent?styleId=${styleId}`, fetchOpts({ method: 'get' }, credentials));
}

export function getLaunchCalendarUpcomingStyles({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getLaunchCalendarUpcomingStyles')) {
  return fetcher(`${url}/slotz/launch-calendar/upcoming-styles-info`, fetchOpts({ method: 'get' }, credentials));
}

const cartErrorTranslator = {
  'cart.validation.exception': 'Oops, something went wrong while updating your cart.',
  'cart.no.stock': 'Sorry, this product is out of stock.',
  'cart.no.style': 'Sorry, this style is out of stock.',
  'asin.not.available': 'Sorry, one of the products in your cart is out of stock, and has been removed.',
  'cannotAddMixedRetailAndEgc':
    'Sorry, but e-Gift Cards may not be combined with other retail items. Please complete your purchase before adding to cart.',
  'max.cart.size.exceeded': `Oops! Your ${cartName} is too full. Please remove some items and try again.`
};

export function translateCartError(response) {
  // Check if the response exists before we try to access a property of it
  if (!response) {
    recordToSplunk(
      buildErrorQueryString('martyCartError', {
        error: 'Sorry, the line went dead!'
      })
    );
    return null;
  }
  // error responses from cart api contain a status code, otherwise it's an OK response
  if (response.statusCode) {
    return cartErrorTranslator[response.id] || 'Sorry, something went wrong!';
  }
  return null;
}
/**
 * Build a fetch options, given initial config and credentials. This will
 * populate necessary auth headers and fetch opts based on given credentials.
 * @param  {Object} [initOpts={}]    inital fetch opts
 * @param  {Object} [credentials={}] mafia credentials
 * @param  {Object} [queryParamCredentials={}] query param mafia credentials
 * @return {Object}                  fetch options
 */
export function fetchOpts(initOpts = {}, credentials: Cookies = {}, queryParamCredentials = {}) {
  const headers = withAuthHeaders(initOpts.headers || {}, credentials, queryParamCredentials);
  const credOpts: { credentials: RequestCredentials } = authHeadersInUse(credentials, queryParamCredentials) ? {} : { credentials: 'include' };
  return { ...initOpts, ...credOpts, headers };
}

export function addClientHeaders(request) {
  const headers = {};
  // if request is present, we _should_ have all three values
  if (request && Object.entries(request).length !== 0) {
    const { clientReferer, clientIp, clientUserAgent } = request;
    headers['x-referer'] = clientReferer;
    headers['x-ip'] = clientIp;
    headers['x-user-agent'] = clientUserAgent;
  }
  return headers;
}

function captchaHeaders({ token, answer }) {
  return {
    [MAFIA_CAPTCHA_TOKEN]: token,
    [MAFIA_CAPTCHA_ANSWER]: answer
  };
}

export function withSession(payload, headers) {
  return { payload, session: headers };
}

export function weblabTrigger({ url }: Mafia, weblab, credentials: Cookies, fetcher = timedFetch('weblabTrigger')) {
  const reqUrl = `${url}/v1/weblab?name=${weblab}`;
  return fetcher(reqUrl, fetchOpts({ method: 'get' }, credentials));
}

export function cartData({ url }: Mafia, querystring = '', credentials: Cookies, fetcher = timedFetch('cart')) {
  const reqUrl = `${url}/v1/cart${querystring}`;

  // if a customer has logged in, pass the header that syncs between desktop and mobile
  const headers = 'x-main' in credentials ? { [MAFIA_RECOGNIZED_TOKEN]: credentials['x-main'] } : {};

  return fetcher(
    reqUrl,
    fetchOpts(
      {
        method: 'get',
        headers: headers
      },
      credentials
    )
  );
}

export function changeCart({ url }: Mafia, querystring = '', data, credentials: Cookies, fetcher = timedFetch('cart')) {
  const reqUrl = `${url}/v1/cart${querystring}`;
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };

  if (isMultiMerchant(data.items || [], merchantId)) {
    headers['merchantId'] = VRSNL_MERCHANTID;
  }

  if ('x-main' in credentials) {
    headers[MAFIA_RECOGNIZED_TOKEN] = credentials['x-main'];
  }

  return fetcher(
    reqUrl,
    fetchOpts(
      {
        method: 'post',
        headers: headers,
        body: JSON.stringify(data)
      },
      credentials
    )
  );
}

export function addresses({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getAddresses')) {
  const reqUrl = `${url}/v1/address`;
  return fetcher(reqUrl, fetchOpts({ method: 'get' }, credentials));
}

export function paymentTypes({ url, shippingAddressId }, credentials: Cookies = {}, fetcher = timedFetch('getPaymentInstruments')) {
  let reqUrl = `${url}/v1/paymentInstruments`;

  if (shippingAddressId) {
    reqUrl += `?associatedAddressId=${shippingAddressId}`;
  }

  return fetcher(reqUrl, fetchOpts({ method: 'get' }, credentials));
}

export function shipOptions({ url }: Mafia, purchaseId, credentials: Cookies = {}, fetcher = timedFetch('shipmentOptions')) {
  const reqUrl = `${url}/v1/shipmentOptions?purchaseId=${purchaseId}`;
  return fetcher(reqUrl, fetchOpts({ method: 'get' }, credentials));
}

export function getPin({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getPin')) {
  const reqUrl = `${url}/freja-laas/pin`;
  return fetcher(reqUrl, fetchOpts({ method: 'post' }, credentials));
}

export function validatePin({ url }: Mafia, pin, credentials: Cookies = {}, fetcher = timedFetch('validatePin')) {
  const reqUrl = `${url}/freja-laas/whitelist?pin=${pin}`;
  return fetcher(reqUrl, fetchOpts({ method: 'get' }, credentials));
}

export function getTrackingLabel({ url }: Mafia, data, credentials: Cookies = {}, fetcher = timedFetch('getTrackingLabel')) {
  const reqUrl = `${url}/freja-laas/generateLabel`;
  return fetcher(
    reqUrl,
    fetchOpts(
      {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      },
      credentials
    )
  );
}

export function logoutCustomer({ url }: Mafia, credentials: Cookies, fetcher = timedFetch('logoutCustomer')) {
  return fetcher(`${url}/v1/zap/logout`, fetchOpts({ method: 'get', mode: 'no-cors' }, credentials));
}

interface ChangeAddressParams {
  mafiaConfig: Mafia;
  address: any;
  forceOriginal: boolean;
}

export function changeAddress(
  params: ChangeAddressParams,
  credentials: Cookies = {},
  saveInvalidAddress = true,
  fetcher = timedFetch('changeAddress')
) {
  const {
    mafiaConfig: { url },
    address,
    forceOriginal
  } = params;
  const reqUrl = `${url}/v1/address?saveInvalidAddress=${saveInvalidAddress}${forceOriginal ? '&forceOriginal=true' : ''}`;
  return fetcher(
    reqUrl,
    fetchOpts(
      {
        method: address.addressId ? 'put' : 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(address)
      },
      credentials
    )
  );
}

export function deleteAddress({ url }: Mafia, credentials: Cookies = {}, addressId, fetcher = timedFetch('deleteAddress')) {
  return fetcher(`${url}/v1/address?addressIds=${addressId}`, fetchOpts({ method: 'DELETE' }, credentials));
}

export function savePayment({ url }: Mafia, params, credentials: Cookies = {}, fetcher = timedFetch('savePaymentInstrument')) {
  const { instrument, addressId, purchaseId, useV2PaymentInstruments } = params;
  const reqUrl = `${url}/${useV2PaymentInstruments ? 'v2' : 'v1'}/paymentInstruments`;

  const { cc, expiration, name, cvv, fullName, isPrimary, paymentInstrumentId } = instrument;

  let expirationMonth;
  let expirationYear;

  if (expiration) {
    ({ expirationMonth, expirationYear } = formatCreditCardExpiration(expiration));
  } else {
    ({ expirationMonth, expirationYear } = instrument);
  }

  let payment = `fullName=${fullName || name}&expirationMonth=${expirationMonth}&expirationYear=${expirationYear}&addressId=${addressId}`;
  if (paymentInstrumentId) {
    payment += `&paymentInstrumentId=${paymentInstrumentId}`;
  } else {
    const formattedCreditCardValue = formatCreditCard(cc);
    const creditCardNumber = formattedCreditCardValue.length ? formattedCreditCardValue : cc;
    payment += `&addCreditCardNumber=${creditCardNumber}`;
  }

  if (cvv) {
    payment += `&addCreditCardVerificationNumber=${cvv}`;
  }

  if (purchaseId) {
    payment += `&purchaseId=${purchaseId}`;
  }

  // Always true since it isn't possible to set primary payment as non-primary, i.e. isPrimary=false
  if (isPrimary) {
    payment += '&isPrimary=true';
  }

  const isTesting = credentials.hasOwnProperty('zappalytics-exclude');
  if (isTesting) {
    payment += '&bypassCardVerification=true';
  }

  return fetcher(
    reqUrl,
    fetchOpts(
      {
        method: paymentInstrumentId ? 'put' : 'post',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payment
      },
      credentials
    )
  );
}

export function creditCard(
  { url }: Mafia,
  credentials: Cookies = {},
  number,
  paymentInstrumentId,
  addressId,
  purchaseId,
  fetcher = timedFetch('associatePaymentInstrument')
) {
  const reqUrl = `${url}/v1/associatePaymentInstrument`;

  const formattedCreditCardValue = formatCreditCard(number);
  const creditCardNumber = formattedCreditCardValue.length ? formattedCreditCardValue : number;
  let data = `paymentInstrumentId=${paymentInstrumentId}&addCreditCardNumber=${creditCardNumber}`;

  if (addressId) {
    data += `&addressId=${addressId}`;
  }

  if (purchaseId) {
    data += `&purchaseId=${purchaseId}`;
  }

  return fetcher(
    reqUrl,
    fetchOpts(
      {
        method: 'post',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data
      },
      credentials
    )
  );
}

export function primaryPayment({ url }: Mafia, fullName, pid, credentials: Cookies = {}, fetcher = timedFetch('makePrimaryPaymentInstrument')) {
  return fetcher(
    `${url}/v1/paymentInstruments`,
    fetchOpts(
      {
        method: 'put',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `paymentInstrumentId=${pid}&isPrimary=true&fullName=${fullName}`
      },
      credentials
    )
  );
}

export function getCustomer({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('customerInfo')) {
  return fetcher(`${url}/v1/customerInfo`, fetchOpts({}, credentials));
}

export function getCustomerAuthentication({ url }: Mafia, credentials, fetcher = timedFetch('customerAuth')) {
  // if at-main is available, use it and ubid-main via queryString, else pass it via cookies
  let queryParams = '';
  if (authHeadersInUse(credentials)) {
    queryParams = `?${stringify({
      'at-main': credentials['at-main'],
      'ubid-main': credentials['ubid-main']
    })}`;
  }
  return fetcher(`${url}/auth/v1/atvalid${queryParams}`, fetchOpts({ method: 'get' }, credentials));
}

export function getGiftCard({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getGiftCard')) {
  const sessionId = credentials['session-id'];
  return fetcher(`${url}/v1/giftCard?sessionId=${sessionId}`, fetchOpts({}, credentials));
}

export function claimGiftCard({ url }: Mafia, giftCardRedeemCode, credentials: Cookies = {}, fetcher = timedFetch('getGiftCard')) {
  return fetcher(
    `${url}/v1/giftCard`,
    fetchOpts(
      {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claimCode: giftCardRedeemCode,
          sessionId: credentials['session-id']
        })
      },
      credentials
    )
  );
}

// My Account API Proxy
export function getOrdersV3(
  { url }: Mafia,
  { page = 1, pageSize = 10, gteDate, lteDate }: Partial<OrderQueryParams>,
  credentials: Cookies = {},
  fetcher = timedFetch('getOrdersV3')
): Promise<Response<OrdersResponse>> {
  return fetcher(
    `${url}/v3/orders?pageSize=${pageSize}&pageNum=${page}${gteDate ? `&gteDate=${gteDate}` : ''}${
      lteDate ? `&lteDate=${lteDate}` : ''
    }&includeTracking=true`,
    fetchOpts({}, credentials)
  );
}

// My Account API Proxy
export function getOrderV3({ url }: Mafia, orderId, credentials: Cookies = {}, fetcher = timedFetch('getOrderV3')) {
  return fetcher(`${url}/v3/order?orderId=${orderId}&includeTracking=true`, fetchOpts({ method: 'get' }, credentials));
}

// My Account Order History Search
export function getOrdersBySearch(
  { url }: Mafia,
  { search, page = 1, pageSize = 10, gteDate, lteDate }: Partial<SearchOrderQueryParams>,
  credentials: Cookies = {},
  fetcher = timedFetch('getOrdersV3')
): Promise<Response<OrdersResponse>> {
  return fetcher(
    `${url}/accountapi/a/orders?${stringify({
      search,
      pageSize,
      page,
      startDate: gteDate,
      endDate: lteDate,
      includeTracking: true
    })}`,
    fetchOpts({}, credentials)
  );
}

export function getOrdersByPurchaseIdV2({ url }: Mafia, pId, credentials: Cookies = {}, fetcher = timedFetch('getOrdersByPurchaseIdV2')) {
  return getOrdersByPurchaseIdByVersion(url, pId, credentials, fetcher, 'v2');
}

function getOrdersByPurchaseIdByVersion(url, pId, credentials: Cookies, fetcher, version) {
  return fetcher(`${url}/${version}/ordersByPurchaseId?includeTracking=true&purchaseId=${pId}`, fetchOpts({ method: 'get' }, credentials));
}

export function getPixelData({ url }: Mafia, purchaseId, credentials: Cookies = {}, fetcher = timedFetch('getPixel')) {
  const reqUrl = `${url}/v1/pixel?purchaseId=${purchaseId}`;
  return fetcher(reqUrl, fetchOpts({ method: 'get' }, credentials));
}

export function cancelOrder({ url }: Mafia, { orderId, items }, credentials: Cookies = {}, fetcher = timedFetch('cancelOrder')) {
  return fetcher(
    `${url}/v2/order/cancel`,
    fetchOpts(
      {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, items })
      },
      credentials
    )
  );
}

// Delete payment instrument for a customer
export function deletePaymentInstrument({ url }: Mafia, paymentId, credentials: Cookies = {}, fetcher = timedFetch('deletePaymentInstrument')) {
  return fetcher(`${url}/v1/paymentInstruments/${paymentId}`, fetchOpts({ method: 'DELETE' }, credentials));
}

// Update payment method on an order
export function updatePayment({ url }: Mafia, { orderId, paymentInstrumentId }, credentials: Cookies = {}, fetcher = timedFetch('updatePayment')) {
  return fetcher(
    `${url}/v1/updatePayment?orderId=${orderId}&paymentInstrumentId=${paymentInstrumentId}`,
    fetchOpts({ method: 'POST' }, credentials)
  );
}

export function fetchTransportationOptions({ url }: Mafia, data, credentials: Cookies = {}, fetcher = timedFetch('fetchTransportationOptions')) {
  const opts = fetchOpts(
    {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: JSON.stringify(data)
    },
    credentials
  );
  return fetcher(`${url}/v1/return/transportOptions`, opts);
}

// New Subscription service calls

const AUTH_ZSUBSCRIPTION_PATH = '/emailsubscriptions/subscriptionsapi/v2/a';
const TOKEN_ZSUBSCRIPTION_PATH = '/emailsubscriptions/subscriptionsapi/v2/n';
const CAPTCHA_ZSUBSCRIPTION_PATH = '/emailsubscriptions/subscriptionsapi/v2/c';

export function getSubscriptionVillageIdiot(
  { url }: Mafia,
  credentials: Cookies = {},
  emailCredentials = {},
  fetcher = timedFetch('getSubscriptionVillageIdiot')
) {
  const opts = fetchOpts(
    {
      method: 'GET',
      headers: { Accept: 'application/json' }
    },
    credentials,
    emailCredentials
  );
  return fetcher(`${url}${AUTH_ZSUBSCRIPTION_PATH}/subscriptions?isCustomerFacing=true`, opts);
}

export function updateSubscriptionVillageIdiot(
  subscriptionsDelta,
  { url }: Mafia,
  credentials: Cookies = {},
  emailCredentials = {},
  fetcher = timedFetch('updateSubscriptionVillageIdiot')
) {
  const subscriptionData = {
    brandSubscriptions: subscriptionsDelta.brandSubscriptions,
    emailSubscriptions: subscriptionsDelta.emailLists,
    stockSubscriptions: subscriptionsDelta.stockSubscriptions,
    upcomingStyleSubscriptions: subscriptionsDelta.upcomingStyleSubscriptions
  };
  const opts = fetchOpts(
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subscriptionData)
    },
    credentials,
    emailCredentials
  );
  return fetcher(`${url}${AUTH_ZSUBSCRIPTION_PATH}/subscriptions`, opts);
}

export function unsubscribeFromAll({ url }: Mafia, credentials: Cookies = {}, emailCredentials = {}, fetcher = timedFetch('unsubscribeFromAll')) {
  const opts = fetchOpts(
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    },
    credentials,
    emailCredentials
  );
  return fetcher(`${url}${AUTH_ZSUBSCRIPTION_PATH}/subscriptions`, opts);
}

function generateZSubscriptionUrl(mafiaUrl, token) {
  if (token) {
    return `${mafiaUrl}${TOKEN_ZSUBSCRIPTION_PATH}/subscriptions?tokenType=${token.tokenTypeOrAction}&token=${token.token}`;
  } else {
    return `${mafiaUrl}${AUTH_ZSUBSCRIPTION_PATH}/subscriptions`;
  }
}

export function getSubscriptionMyAccount({ url }: Mafia, credentials: Cookies = {}, token = null, fetcher = timedFetch('getSubscriptionMyAccount')) {
  const opts = fetchOpts(
    {
      method: 'GET',
      headers: { Accept: 'application/json' }
    },
    credentials
  );
  if (token) {
    return fetcher(`${generateZSubscriptionUrl(url, token)}`, opts);
  }
  return fetcher(`${generateZSubscriptionUrl(url, token)}?isCustomerFacing=true`, opts);
}

export function updateSubscriptionMyAccount(
  subscriptionsDelta,
  { url }: Mafia,
  credentials: Cookies = {},
  token = null,
  fetcher = timedFetch('updateSubscriptionMyAccount')
) {
  const opts = fetchOpts(
    {
      method: token ? 'POST' : 'PUT',
      headers: { Accept: 'application/json' },
      body: JSON.stringify(subscriptionsDelta)
    },
    credentials
  );
  return fetcher(`${generateZSubscriptionUrl(url, token)}`, opts);
}

export function updateSubscriptionMyAccountUnsubscribeAll(
  { url }: Mafia,
  credentials: Cookies,
  token,
  fetcher = timedFetch('updateSubscriptionMyAccountUnsubscribeAll')
) {
  const opts = fetchOpts(
    {
      method: 'DELETE',
      headers: { Accept: 'application/json' }
    },
    credentials
  );
  return fetcher(`${url}${TOKEN_ZSUBSCRIPTION_PATH}/subscriptions/${token.tokenTypeOrAction}/${token.token}`, opts);
}

export function updateSubscriptionMyAccountOptOut(
  { url }: Mafia,
  credentials: Cookies,
  token,
  fetcher = timedFetch('updateSubscriptionMyAccountOptOut')
) {
  const opts = fetchOpts(
    {
      method: 'POST',
      headers: { Accept: 'application/json' }
    },
    credentials
  );
  return fetcher(`${url}${TOKEN_ZSUBSCRIPTION_PATH}/subscriptions/opt-out?token=${token}`, opts);
}

export function requestSubscriptionSignupCaptcha({ url }: Mafia, fetcher = timedFetch('requestSubscriptionSignupCaptcha')) {
  return requestCaptcha(url, `${CAPTCHA_ZSUBSCRIPTION_PATH}/subscribeToMarketingList`, fetcher, 'POST');
}

export function submitSubscriptionSignup(
  { url }: Mafia,
  credentials: Cookies,
  { recipientEmail, answer, token },
  fetcher = timedFetch('submitSubscriptionSignup')
) {
  const opts = fetchOpts(
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...captchaHeaders({ token, answer })
      },
      body: JSON.stringify({ emailAddress: recipientEmail })
    },
    credentials
  );
  return fetcher(`${url}${CAPTCHA_ZSUBSCRIPTION_PATH}/subscribeToMarketingList`, opts);
}

// Returns
export function getPrereturnInfo({ url }: Mafia, orderId, orderItemIds, credentials: Cookies = {}, fetcher = timedFetch('getPrereturnInfo')) {
  return fetcher(`${url}/v1/return/initiate?orderId=${orderId}&asins=${orderItemIds}`, fetchOpts({}, credentials));
}

export function placeReturn({ url }: Mafia, reqData, credentials: Cookies, fetcher = timedFetch('placeReturn')) {
  const reqUrl = `${url}/v1/return/submit`;
  return fetcher(
    reqUrl,
    fetchOpts(
      {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqData)
      },
      credentials
    )
  );
}

export function getRewardsTerms({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getRewardsTerms')) {
  const pageName = 'rewards-terms-of-use';
  return fetcher(`${url}/v1/getPage?pageName=${pageName}`, fetchOpts({}, credentials));
}

export function returnLabelInfo({ url }: Mafia, { rmaId, contractId }, credentials: Cookies = {}, fetcher = timedFetch('returnLabelInfo')) {
  const labelParam = contractId ? `contractId=${contractId}` : `rmaId=${rmaId}`;
  return fetcher(`${url}/v1/return/fetch/label?${labelParam}`, fetchOpts({}, credentials));
}

export function getReturnLabelImageObjectUrl(labelImageUrl, credentials: Cookies = {}, fetcher = timedFetch('getReturnLabelImageObjectUrl')) {
  if (!labelImageUrl) {
    return Promise.resolve(labelImageUrl);
  }
  return fetcher(labelImageUrl, fetchOpts({}, credentials))
    .then(fetchApiNotAuthenticatedMiddleware)
    .then(response => response.blob())
    .then(blob => URL.createObjectURL(blob));
}

export function getRewardsV2({ url }: Mafia, akitaKey: string, credentials: Cookies = {}, fetcher = timedFetch('getRewardsV2')) {
  return fetcher(
    `${url}/akitav2/slotz/recognized/v1/vip_membership`,
    fetchOpts(
      {
        headers: { 'x-api-key': akitaKey }
      },
      credentials
    )
  );
}

export function getPerks({ url }: Mafia, akitaKey: string, credentials: Cookies = {}, fetcher = timedFetch('getPerks')) {
  return fetcher(
    `${url}/akitav2/slotz/recognized/v1/perks`,
    fetchOpts(
      {
        headers: { 'x-api-key': akitaKey }
      },
      credentials
    )
  );
}

export function enrollRewardsV2({ url }: Mafia, akitaKey: string, credentials: Cookies = {}, fetcher = timedFetch('signupForRewardsV2')) {
  return fetcher(
    `${url}/akitav2/slotz/full/v1/vip_membership/enroll`,
    fetchOpts(
      {
        method: 'post',
        headers: { 'Content-Type': 'application/json', 'x-api-key': akitaKey }
      },
      credentials
    )
  );
}

export function enrollRewardsWithEmailTokenV2(
  { url }: Mafia,
  akitaKey: string,
  credentials: Cookies = {},
  emailAuthToken,
  fetcher = timedFetch('signupForRewardsV2')
) {
  const queryParamCredentials = emailAuthToken ? { auth_token: emailAuthToken } : {};
  return fetcher(
    `${url}/akitav2/slotz/full/v1/vip_membership/enroll`,
    fetchOpts(
      {
        method: 'post',
        headers: { 'Content-Type': 'application/json', 'x-api-key': akitaKey }
      },
      credentials,
      queryParamCredentials
    )
  );
}

export function getRewardsEstimateV2(
  { url }: Mafia,
  akitaKey: string,
  credentials: Cookies = {},
  data,
  fetcher = timedFetch('getRewardsEstimateV2')
) {
  return fetcher(
    `${url}/akitav2/slotz/optional/v1/vip_points/estimate`,
    fetchOpts(
      {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': akitaKey
        },
        body: JSON.stringify(data)
      },
      credentials
    )
  );
}

export function setRefundOnFirstScanPreference(
  { url }: Mafia,
  akitaKey: string,
  credentials: Cookies = {},
  rofsPreference,
  fetcher = timedFetch('setRefundOnFirstScanPreference')
) {
  const headers = {
    'x-api-key': akitaKey,
    'Content-Type': 'application/json'
  };

  const opts = fetchOpts(
    {
      method: 'post',
      headers,
      body: JSON.stringify({ preference: rofsPreference })
    },
    credentials
  );

  const reqUrl = `${url}/akita/slotz/full/v1/customers/rofs_preference`;
  return fetcher(reqUrl, opts);
}

export function getEGiftCardDesigns({ url }: Mafia, fetcher = timedFetch('getEGCDesigns')) {
  return fetcher(`${url}/v1/eGiftCardDesigns`);
}

export function getPrimeStatus({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getPrimeStatus')) {
  const headers = {};

  if ('x-main' in credentials) {
    headers[MAFIA_RECOGNIZED_TOKEN] = credentials['x-main'];
  }

  return fetcher(`${url}/zap/lwa/customer/primeStatus`, {
    credentials: 'include',
    headers,
    fetcher
  });
}

export function getBrandPromo({ url }: Mafia, brandId: string, fetcher = timedFetch('getBrandPromo')) {
  return fetcher(`${url}/v1/content?pageLayout=Simple&slotName=primary-1&pageName=pdp-brand-promo-${brandId}`);
}

export function getZcsSlot({ url, siteId, subsiteId }: Mafia, credentials: Cookies = {}, params, fetcher = timedFetch('getZcsSlot')) {
  return fetcher(`${url}/v1/content?${stringify({ ...params, siteId, subsiteId })}`, fetchOpts({}, credentials));
}

// new ZSubscriptionService APIs

export function subscribeToStockNotificationZSub(
  { url }: Mafia,
  data,
  credentials: Cookies = {},
  fetcher = timedFetch('subscribeToStockNotificationZSub')
) {
  const additionalHeaders = {};
  if ('x-main' in credentials) {
    // for cart OOS notify me without being authed
    additionalHeaders[MAFIA_RECOGNIZED_TOKEN] = credentials['x-main'];
  }
  return sendJsonDataWithCreds(`${url}${AUTH_ZSUBSCRIPTION_PATH}/subscriptions/stocks`, data, { credentials, fetcher, additionalHeaders });
}

export function subscribeToUpcomingStylesNotificationZSub(
  { url }: Mafia,
  data,
  credentials: Cookies = {},
  fetcher = timedFetch('subscribeToUpcomingStylesNotificationZSub')
) {
  const additionalHeaders = {};
  if ('x-main' in credentials) {
    // for cart OOS notify me without being authed
    additionalHeaders[MAFIA_RECOGNIZED_TOKEN] = credentials['x-main'];
  }

  return sendJsonDataWithCreds(`${url}${AUTH_ZSUBSCRIPTION_PATH}/subscriptions/upcoming-styles`, data, { credentials, fetcher, additionalHeaders });
}

export function subscribeToListZSub({ url }: Mafia, data, credentials: Cookies = {}, fetcher = timedFetch('subscribeToListZSub')) {
  return sendJsonDataWithCreds(`${url}${AUTH_ZSUBSCRIPTION_PATH}/subscriptions/lists`, data, { credentials, fetcher });
}

export function subscribeToBrandZSub({ url }: Mafia, data, credentials: Cookies = {}, fetcher = timedFetch('subscribeToBrandZSub')) {
  return sendJsonDataWithCreds(`${url}${AUTH_ZSUBSCRIPTION_PATH}/subscriptions/brands`, data, { credentials, fetcher });
}

export function subscribeToMarketingListZSub({ url }: Mafia, data, credentials: Cookies = {}, fetcher = timedFetch('subscribeToMarketingListZSub')) {
  return sendJsonDataWithCreds(`${url}${AUTH_ZSUBSCRIPTION_PATH}/subscribeToMarketingList`, data, { credentials, fetcher });
}

export function postTellAFriend({ url }: Mafia, data, captchaResponseData, credentials: Cookies = {}, fetcher = timedFetch('tellAFriend')) {
  return sendJsonDataWithCreds(`${url}${TELL_A_FRIEND_ENDPOINT}`, data, {
    credentials,
    fetcher,
    additionalHeaders: captchaHeaders(captchaResponseData)
  });
}

function requestCaptcha(mafiaBaseUrl, path, fetcher, method = 'get', credentials: Cookies = {}) {
  return fetcher(
    `${mafiaBaseUrl}${path}`,
    fetchOpts(
      {
        method,
        headers: { [MAFIA_CAPTCHA_REQ]: 'v1', [MAFIA_CAPTCHA_AUDIO_REQ]: 'v1' }
      },
      credentials
    )
  );
}

export function requestTellAFriendCaptcha({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('requestCaptcha')) {
  return requestCaptcha(url, TELL_A_FRIEND_ENDPOINT, fetcher, 'post', credentials);
}

export function postReportAnError({ url }: Mafia, data, captchaResponseData, credentials: Cookies = {}, fetcher = timedFetch('reportAnAError')) {
  return sendJsonDataWithCreds(`${url}${REPORT_AN_ERROR_ENDPOINT}`, data, {
    credentials,
    fetcher,
    additionalHeaders: captchaHeaders(captchaResponseData)
  });
}

export function requestReportAnErrorCaptcha({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('requestCaptcha')) {
  return requestCaptcha(url, REPORT_AN_ERROR_ENDPOINT, fetcher, 'post', credentials);
}

// Called when MelodyNewsfeed component mounts
export function postNewsfeedImpression(
  { url }: Mafia,
  { type, eventId, lineItemId, completed },
  credentials: Cookies = {},
  fetcher = timedFetch('newsfeedImpression')
) {
  const data = {
    [type]: [
      {
        lineItemId,
        eventId,
        completed
      }
    ]
  };
  return sendJsonDataWithCreds(`${url}/cronkite/newsFeed`, data, {
    credentials,
    fetcher
  });
}

export function postFitSurveyReply({ url }: Mafia, data, credentials: Cookies = {}, fetcher = timedFetch('newsfeedFitSurveyReply')) {
  return sendJsonDataWithCreds(`${url}${ENDPOINT_OPAL_EXPLICITFITS}`, data, {
    credentials,
    fetcher
  });
}

export function updateFitSurveyReply(
  { url }: Mafia,
  { id, deleteFit },
  data,
  credentials: Cookies = {},
  fetcher = timedFetch('newsfeedUpdateFitSurveyReply')
) {
  return sendJsonDataWithCreds(`${url}${ENDPOINT_OPAL_EXPLICITFITS}/${id}`, data, { credentials, fetcher, method: deleteFit ? 'delete' : 'put' });
}

export function dismissNewsfeed({ url }: Mafia, { type, eventId, lineItemId }, credentials: Cookies = {}, fetcher = timedFetch('dismissNewsfeed')) {
  const reqUrl = `${url}/cronkite/newsFeed?type=${type}&eventId=${eventId}&lineItemId=${lineItemId}`;
  return fetcher(
    reqUrl,
    fetchOpts(
      {
        method: 'delete',
        headers: { 'Content-Type': 'application/json' }
      },
      credentials
    )
  );
}

export function cartCount({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getCartCount')) {
  return fetcher(`${url}/v1/getCartItemsCount`, fetchOpts({}, credentials));
}

export function recommendationsSearch(
  { url }: Mafia,
  credentials: Cookies = {},
  recoSearchFeatureFlag: boolean = false,
  numberOfRecos = 25,
  slotName = 'zap-hp-vh',
  searchText = 'empty',
  fetcher = timedFetch('recommendationsSearch')
) {
  const janusUrl = `${url}/janus/recos/get?limit=${numberOfRecos}&widgets=${slotName}`;
  const mafiaUrl = `${url}/v1/recommendationsSearch?&searchText=${searchText}&slotName=${slotName}&numberOfRecos=${numberOfRecos}`;

  return fetcher(recoSearchFeatureFlag ? janusUrl : mafiaUrl, fetchOpts({}, credentials));
}

export function getExplicitFits({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getExplicitFits')) {
  return fetcher(`${url}${ENDPOINT_OPAL_EXPLICITFITS}`, fetchOpts({}, credentials));
}

export function getLandingPageInfo(
  { url, siteId, subsiteId }: Mafia,
  { pageName },
  credentials: Cookies = {},
  request = {},
  location = {},
  fetcher = timedFetch('getLandingPageInfo')
) {
  const qs = stringify(
    {
      ...location.query, // pass query params to mafia
      siteId,
      subsiteId,
      pageName
    },
    { sort: false }
  );
  const reqUrl = `${url}/v1/getPage?${qs}`;
  const headers = addClientHeaders(request);
  // Attatch 'geo' cookie to server-side marty calls as 'zfc-geo' header
  translateCookieToMafiaHeader(headers, credentials, 'zfc-geo', 'geo');
  return fetcher(reqUrl, fetchOpts({ headers }, credentials));
}

export function getTaxonomyBrandPageInfo(
  { url, siteId, subsiteId }: Mafia,
  brandId: string,
  credentials: Cookies = {},
  request = {},
  fetcher = timedFetch('getTaxonomyBrandPageInfo')
) {
  const qs = stringify({
    pageName: 'generic-brands-template',
    brandId,
    siteId,
    subsiteId
  });
  const reqUrl = `${url}/v1/getPage?${qs}`;
  const headers = addClientHeaders(request);
  // Attatch 'geo' cookie to server-side marty calls as 'zfc-geo' header
  translateCookieToMafiaHeader(headers, credentials, 'zfc-geo', 'geo');
  return fetcher(reqUrl, fetchOpts({ headers }, credentials));
}

export function getSymphonyPreviewInfo({ url }: Mafia, slot, credentials: Cookies = {}, fetcher = timedFetch('getSymphonyPreviewInfo')) {
  const reqUrl = `${url}/zcs/preview?slot_center-1=${slot}`;
  return fetcher(reqUrl, fetchOpts({}, credentials));
}

export function getSymphonyPdpComponents(
  { url, siteId, subsiteId }: Mafia,
  { productId, styleId },
  credentials: Cookies = {},
  fetcher = timedFetch('getSymphonyPdpComponents')
) {
  const qs = stringify({
    product: productId,
    style: styleId,
    siteId,
    subsiteId
  });
  const reqUrl = `${url}/zcs/productPage?${qs}`;
  return fetcher(reqUrl, fetchOpts({}, credentials));
}

export function getSymphonySlots(
  { url, siteId, subsiteId }: Mafia,
  { ...params } = {},
  credentials: Cookies = {},
  fetcher = timedFetch('getSymphonyPdpComponents')
) {
  const qs = stringify({
    siteId,
    subsiteId,
    ...params
  });
  const reqUrl = `${url}/zcs/getSlots?${qs}`;
  return fetcher(reqUrl, fetchOpts({}, credentials));
}

export function getSymphonySearchComponents(
  { url }: Mafia,
  { term, path },
  credentials: Cookies = {},
  request = {},
  fetcher = timedFetch('getSymphonySearchComponents')
) {
  const queryObj = {
    pageName: term,
    pageLayout: 'search'
  };

  if (path) {
    if (path.includes('.zso')) {
      queryObj['ProductSearchZso'] = path;
    } else {
      queryObj['ProductSearchTerm'] = path;
    }
  }
  const query = stringify(queryObj);
  const reqUrl = `${url}/zcs/getSlots?${query}`;
  const headers = addClientHeaders(request);
  return fetcher(reqUrl, fetchOpts({ headers }, credentials));
}

interface JanusFetchOpts {
  params: JanusParams;
  widgets: string;
  limit?: number | string;
  credentials?: any; // TODO ts type this with creds;
  dispatch: ThunkDispatch<AppState, void, AnyAction>;
  getState: () => AppState;
}

export function getJanusRecos(
  { url }: Mafia,
  { params, widgets, limit = 5, credentials = {}, dispatch, getState }: JanusFetchOpts,
  fetcher = timedFetch('janusRecos')
): Promise<Recos> {
  const query = stringify(
    {
      widgets,
      limit,
      ...params
    },
    { encode: false }
  );
  return fetcher(
    `${url}/janus/recos/get?${query}`,
    fetchOpts(
      {
        credentials: 'include'
      },
      credentials
    )
  )
    .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
    .then(fetchErrorMiddleware);
}

export async function getSurveyQuestions({ url }: Mafia, surveyName, fetcher = timedFetch('getSurveyQuestions'), abortController?) {
  const reqUrl = `${url}/auscult/unauth/survey/take/${surveyName}`;
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  const response = await fetcher(reqUrl, { headers: headers, signal: abortController?.signal });
  const json = await response.json();
  return json;
}

export async function saveSurveyQuestionResponse({ url }: Mafia, params, credentials: Cookies, fetcher = timedFetch('saveSurveyQuestionResponse')) {
  const { csrfToken, feedback, source, surveyId, questionId, sessionReplayLink } = params;
  const reqUrl = `${url}/auscult/unauth/survey/take/${surveyId}`;

  const headers = { 'Content-Type': 'application/json' };
  const submission = {
    _csrf_token: csrfToken,
    id: surveyId,
    take_survey: {
      [`q${questionId}`]: feedback,
      [`qid${questionId}`]: `${questionId}`
    },
    metadata: { source, session_replay_link: sessionReplayLink }
  };
  const opts = fetchOpts(
    {
      method: 'post',
      headers,
      body: JSON.stringify(submission)
    },
    credentials
  );
  const response = await fetcher(reqUrl, opts);
  const json = await response.json();
  return json;
}

export async function saveMultiSurveyQuestionResponse(
  { url }: Mafia,
  params,
  credentials: Cookies,
  fetcher = timedFetch('saveSurveyQuestionResponse')
) {
  const { csrfToken, responses, surveyId } = params;
  const reqUrl = `${url}/auscult/unauth/survey/take/${surveyId}`;

  const takeSurveyData = Object.entries(responses).reduce(
    (acc, [key, val]) => ({
      ...acc,
      [`q${key}`]: val,
      [`qid${key}`]: key
    }),
    []
  );

  const headers = { 'Content-Type': 'application/json' };
  const submission = {
    _csrf_token: csrfToken,
    id: surveyId,
    take_survey: {
      ...takeSurveyData
    }
  };
  const opts = fetchOpts(
    {
      method: 'post',
      headers,
      body: JSON.stringify(submission)
    },
    credentials
  );
  const response = await fetcher(reqUrl, opts);
  const json = await response.json();
  return json;
}

// Exchanges

export function initiateExchange(
  { url }: Mafia,
  orderId: string,
  orderItemId: string,
  credentials: Cookies = {},
  fetcher = timedFetch('initiateExchange')
) {
  return fetcher(`${url}/v1/exchange/initiate?orderId=${orderId}&asins=${orderItemId}`, fetchOpts({}, credentials));
}

export function placeExchange({ url }: Mafia, reqData: object, credentials: Cookies = {}, fetcher = timedFetch('placeExchange')) {
  const reqUrl = `${url}/v1/exchange/submit`;
  return fetcher(
    reqUrl,
    fetchOpts(
      {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqData)
      },
      credentials
    )
  );
}

export function previewExchangeEligibility(
  { url }: Mafia,
  orderId: string,
  credentials: Cookies = {},
  fetcher = timedFetch('previewExchangeEligibility')
) {
  return fetcher(`${url}/v1/exchange/previewEligibility?orderId=${orderId}`, fetchOpts({}, credentials));
}

/**
 * Get product sizing prediction
 * @param  {object}   url             sizing prediction api endpoint
 * @param  {string}   productId       product id
 * @param  {function} [fetcher=fetch] fetch or fetch like implementation
 * @return {Promise}                   promise
 */
export function genericSizeBias({ url }: Mafia, productId: string, credentials: Cookies = {}, fetcher = timedFetch('genericSizingPrediction')) {
  const queryParams = {
    productId
  };

  const query = stringify(queryParams);
  const reqUrl = `${url}/zcs/getSizeBias?${query}`;
  return fetcher(
    reqUrl,
    fetchOpts(
      {
        credentials: 'include'
      },
      credentials
    )
  );
}

export function fetchExchangeOrderId({ url }: Mafia, contractId: string, credentials: Cookies = {}, fetcher = timedFetch('viewExchangeOrderId')) {
  return fetcher(`${url}/v1/exchange/fetch?contractId=${contractId}`, fetchOpts({}, credentials));
}

// Zappos At Work APIs

export function getZawPromotions({ url }: Mafia, promotionIds: string[], credentials: Cookies = {}, fetcher = timedFetch('getZawPromotions')) {
  const promotionIdQueryParams = [];
  new Set(promotionIds).forEach(promoId => {
    promotionIdQueryParams.push(`promotionIds=${promoId}`);
  });
  const queryParam = promotionIdQueryParams.join('&');
  return fetcher(`${url}/zaw/v1/getPromotions?${queryParam}`, fetchOpts({}, credentials));
}

export async function getInfluencerHashtags({ url }: Mafia, credentials: Cookies = {}, fetcher = timedFetch('getInfluencerHashtags')) {
  return fetcher(`${url}/influencercontent/n/tags`, fetchOpts({}, credentials));
}

// Visual Search

export function getVsPresignedUrl(
  { url }: Mafia,
  queryId: string,
  credentials: Cookies = {},
  signal: AbortSignal,
  fetcher = timedFetch('getVsPresignedUrl')
): Promise<Response<UploadImageResponse>> {
  return sendJsonDataWithCreds(
    `${url}/slotz/vs/imageUploadUrl`,
    { queryId: queryId, clientId: 'VisualSearch' },
    {
      credentials,
      fetcher,
      method: 'post',
      signal
    }
  );
}

export function getBoundingBoxes(
  { url }: Mafia,
  reqData: object,
  credentials: Cookies = {},
  signal: AbortSignal,
  fetcher = timedFetch('getBoundingBoxes')
): Promise<Response<BoundingBoxApiResponse>> {
  return fetcher(
    `${url}/slotz/vs/getImageBoundingBox`,
    fetchOpts(
      {
        credentials: 'include',
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqData),
        signal
      },
      credentials
    )
  );
}

export function getStyleIdsForBB(
  { url }: Mafia,
  reqData: object,
  credentials: Cookies = {},
  signal: AbortSignal,
  fetcher = timedFetch('getImageMatchSearch')
): Promise<Response<ImageMatchSearchResponse>> {
  return fetcher(
    `${url}/slotz/vs/imageMatchSearch`,
    fetchOpts(
      {
        credentials: 'include',
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqData),
        signal
      },
      credentials
    )
  );
}

export const cancelReturn = async (
  { url }: Mafia,
  data: object,
  credentials: Cookies = {},
  fetcher = timedFetch('placeCancelReturn')
): Promise<Response> => {
  const fetchUrl = `${url}/v1/return/cancelReturnItems`;
  const options = fetchOpts({ method: 'post', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }, credentials);

  return fetcher(fetchUrl, options);
};
